import { z } from 'zod';
/**
 * SQL Server has no native enum type, so Prisma cannot generate enums for it.
 * These unions are the single source of truth instead — the database stores
 * strings guarded by CHECK constraints, and both the API and the web app
 * import from here. Change a value and both sides fail to compile.
 */
export const ROLES = ['Member', 'Moderator', 'Administrator'];
export const roleSchema = z.enum(ROLES);
/** Roles that can be granted to a user. `Member` is implicit for anyone with membership. */
export const GRANTABLE_ROLES = ['Moderator', 'Administrator'];
export const grantableRoleSchema = z.enum(GRANTABLE_ROLES);
/**
 * Membership and account state.
 * - Invited  : row created by an administrator, has never signed in
 * - Active   : signed in and permitted to participate
 * - Suspended: may read, may not post or comment. Moderator action, has an expiry
 * - Removed  : membership revoked by an administrator. Reversible by re-inviting
 * - Inactive : set by the leaver reconciliation job when Entra says they have gone
 */
export const USER_STATUSES = ['Invited', 'Active', 'Suspended', 'Removed', 'Inactive'];
export const userStatusSchema = z.enum(USER_STATUSES);
/** Whether unlisted employees may sign in and self-provision. */
export const MEMBERSHIP_MODES = ['InviteOnly', 'Open'];
export const membershipModeSchema = z.enum(MEMBERSHIP_MODES);
/**
 * Which Entra tenant a ForumUser signed in through. Workforce is the existing
 * single-tenant sign-in; External is a business partner invited by email
 * through the separate Entra External ID (CIAM) tenant — see ExternalInvite.
 */
export const IDENTITY_SOURCES = ['Workforce', 'External'];
export const identitySourceSchema = z.enum(IDENTITY_SOURCES);
/** Pending | Claimed — see ExternalInvite. */
export const EXTERNAL_INVITE_STATUSES = ['Pending', 'Claimed'];
export const externalInviteStatusSchema = z.enum(EXTERNAL_INVITE_STATUSES);
/** Where a piece of content sits in the moderation workflow. */
export const MODERATION_STATES = [
    'Draft',
    'PendingReview',
    'Published',
    'Rejected',
    'Removed',
];
export const moderationStateSchema = z.enum(MODERATION_STATES);
/** Where a topic sits in the delivery workflow, independent of moderation. */
export const TOPIC_STATUSES = ['Open', 'UnderReview', 'Actioned', 'Closed'];
export const topicStatusSchema = z.enum(TOPIC_STATUSES);
/** The five categories from the Business Partner Forum requirements. */
export const CATEGORY_KEYS = ['Enhancement', 'Bug', 'Compliment', 'Struggle', 'General'];
export const categoryKeySchema = z.enum(CATEGORY_KEYS);
/**
 * What a trigger-word match does.
 * - Block  : content is held before publication and cannot be seen until reviewed
 * - Flag   : content publishes but enters the moderation queue
 * - Monitor: content publishes, no queue entry, counted for tuning only
 */
export const TRIGGER_SEVERITIES = ['Block', 'Flag', 'Monitor'];
export const triggerSeveritySchema = z.enum(TRIGGER_SEVERITIES);
export const TRIGGER_MATCH_TYPES = ['Word', 'Phrase', 'Regex'];
export const triggerMatchTypeSchema = z.enum(TRIGGER_MATCH_TYPES);
/** Languages the trigger-word list covers. English alone would miss most of it. */
export const TRIGGER_LANGUAGES = ['en', 'af', 'zu', 'xh'];
export const triggerLanguageSchema = z.enum(TRIGGER_LANGUAGES);
/** Malware scan state for an uploaded attachment. */
export const SCAN_STATES = ['Pending', 'Clean', 'Infected', 'Failed'];
export const scanStateSchema = z.enum(SCAN_STATES);
/** Auditable moderation actions. Append-only — see ModerationEvent. */
export const MODERATION_ACTIONS = [
    'AutoFlagged',
    'AutoBlocked',
    'UserReported',
    'Approved',
    'ReturnedToAuthor',
    'EditedByModerator',
    'Rejected',
    'Removed',
    'Restored',
    // Malware-scan verdicts on an Attachment, from the Defender for Storage
    // webhook. Infected reuses Removed (the blob is deleted synchronously,
    // same as any other takedown) rather than adding a third value here.
    'ScanCleared',
    'ScanFailed',
];
export const moderationActionSchema = z.enum(MODERATION_ACTIONS);
/** Auditable access-control actions. Append-only — see AccessAuditEvent. */
export const ACCESS_ACTIONS = [
    'Invited',
    'MembershipRemoved',
    'MembershipRestored',
    'RoleGranted',
    'RoleRevoked',
    'Suspended',
    'SuspensionLifted',
    'DeactivatedByLeaverJob',
    'DirectorySearched',
    'SignedIn',
    'SignInDenied',
    'CategoryUpdated',
    'CategoryCreatedOnStartup',
    'TriggerRuleCreatedOnStartup',
    'TriggerRuleCreated',
    'TriggerRuleUpdated',
    'MainCategoryCreated',
    'MainCategoryUpdated',
    'ExternalInviteSent',
    'ExternalInviteClaimed',
];
export const accessActionSchema = z.enum(ACCESS_ACTIONS);
//# sourceMappingURL=enums.js.map